import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { CircularProgress, Avatar, Box, TextField } from "@mui/material";
import WarningIcon from '@mui/icons-material/Warning';
import { red } from '@mui/material/colors';
import BrokenImage from '@mui/icons-material/BrokenImage';

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
import IconButton from "@mui/material/IconButton";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from "sweetalert2";
import BusinessCategoryAddModel from "./business_category_add_model";
import BusinessCategoryEditModel from "./business_category_edit_model";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";
import { useNavigate } from "react-router-dom";
import LogoutButton from "layouts/logout";
import { ServerBaseUrlForStaticImages } from "constants";

const BASE_IMAGE_URL = ServerBaseUrlForStaticImages;

const AvatarWithLoader = ({ src, alt }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [hasLoadedSuccessfully, setHasLoadedSuccessfully] = useState(false);

  useEffect(() => {
    if (src) {
      setLoading(true);
      setError(false);
      setHasLoadedSuccessfully(false);
    } else {
      setLoading(false);
      setError(true);
      setHasLoadedSuccessfully(false);
    }
  }, [src]);

  const handleImageLoad = () => {
    setLoading(false);
    setError(false);
    setHasLoadedSuccessfully(true);
  };

  const handleImageError = () => {
    setLoading(false);
    setError(true);
    setHasLoadedSuccessfully(false);
  };

  const shouldShowLoader = loading && !error && !hasLoadedSuccessfully;
  const hideActualImage = (loading && !hasLoadedSuccessfully) || error;

  if (!src) {
    return (
      <Avatar
        alt={alt}
        sx={{ width: 40, height: 40, margin: "auto", bgcolor: 'grey.300' }}
        variant="rounded"
      >
        <BrokenImage color="action" sx={{ fontSize: 24 }} />
      </Avatar>
    );
  }

  return (
    <Box sx={{
      position: 'relative',
      width: 40,
      height: 40,
      margin: 'auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '8px',
      overflow: 'hidden',
    }}>
      {shouldShowLoader && (
        <CircularProgress size={20} sx={{ position: 'absolute', color: 'info.main' }} />
      )}
      {error && (
        <Avatar
          alt={alt}
          sx={{ width: 40, height: 40, margin: "auto", bgcolor: 'error.light' }}
          variant="rounded"
        >
          <BrokenImage sx={{ fontSize: 24, color: 'error.dark' }} />
        </Avatar>
      )}
      {!error && (
        <Avatar
          src={src}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          sx={{
            width: 40,
            height: 40,
            margin: "auto",
            variant: "rounded",
            visibility: hideActualImage ? 'hidden' : 'visible',
            position: 'absolute',
          }}
          variant="rounded"
        />
      )}
    </Box>
  );
};


export default function BusinessCategory() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    disableBackNavigation();
    fetchBusinessCategories();
  }, []);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editInput, setEditInput] = useState({});
  const [loading, setLoading] = useState(true);
  const [businessCategories, setBusinessCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);
  const navigate = useNavigate();

  const handleCloseAddModal = () => {
    setOpenAddModal(false);
    fetchBusinessCategories();
  };

  const handleClose = () => {
    setEditInput({});
    setOpenAddModal(false);
    setOpenEditModal(false);
    setOpenDeleteModal(false);
    fetchBusinessCategories();
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const columns = useMemo(() => [
    {
      Header: "S.No",
      accessor: "s_no",
      align: "center",
      Cell: ({ row }) => row.index + 1,
    },
    {
      Header: "Image",
      accessor: "image",
      align: "center",
      Cell: ({ row }) => (
        <AvatarWithLoader
          src={row.original.image_url || null}
          alt={row.original.business_category}
        />
      ),
    },
    {
      Header: "Business Category",
      accessor: "business_category",
      sortable: true,
      align: "center",
    },
    {
      Header: "Options",
      accessor: "options",
      align: "center",
      Cell: ({ row }) => (
        <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <IconButton
            color="success"
            onClick={() => {
              setOpenEditModal(true);
              setEditInput(row.original);
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => {
              setDeleteCategoryId(row.original.business_category_id);
              setOpenDeleteModal(true);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      ),
    },
  ], []);

  const mappedData = useMemo(() => {
    return Array.isArray(businessCategories) && businessCategories.length > 0
      ? businessCategories.map((cat) => ({
          business_category_id: cat.id,
          business_category: cat.business_category,
          image_url: cat.image_url
            ? `${BASE_IMAGE_URL.replace(/\/$/, "")}/${cat.image_url.replace(/^\/+/, "")}`
            : null,
        }))
      : [];
  }, [businessCategories]);

  useEffect(() => {
    if (searchQuery) {
      const lowerCaseQuery = searchQuery.toLowerCase();
      const newFilteredCategories = mappedData.filter(category => {
        return (
          category.business_category.toLowerCase().includes(lowerCaseQuery)
        );
      });
      setFilteredCategories(newFilteredCategories);
    } else {
      setFilteredCategories(mappedData);
    }
    setPage(0);
  }, [searchQuery, mappedData]);


  const fetchBusinessCategories = async () => {
    setLoading(true);
    try {
      const response = await api.get("/vendor_management/business_category/");
      if (response.data.success === 1) {
        setBusinessCategories(response.data.business_catgory);
      } else {
        Swal.fire("Error", "Failed to load business categories. Please try again later.", "error");
      }
    } catch (error) {
      console.error("Error fetching business categories:", error);
      Swal.fire("Error", "Failed to load business categories. Please try again later.", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteBusinessCategory = async (event) => {
    event.preventDefault();
    try {
      handleClose();
      const response = await api.delete(`/vendor_management/business_category/${deleteCategoryId}`);
      if (response.data.status === 1) {
        Swal.fire({
          title: response.data.msg,
          icon: "success",
          timer: 1500,
        });
        fetchBusinessCategories();
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.msg,
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting business category:", error);
      Swal.fire({
        title: "Error",
        text: "An unexpected error occurred",
        icon: "error",
      });
    }
  };

  const handleRowsPerPageChange = (event, newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedTableData = filteredCategories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);


  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton onLogout={() => navigate("/login")} />
      </MDBox>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                <MDTypography variant="h6" color="white">
                  Business Categories
                </MDTypography>
              </MDBox>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}> 
               
                <TextField
                  label="Search categories..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ width: '300px' }}
                />
                 <MDButton color="success" onClick={() => setOpenAddModal(true)}>
                  Add Business Category
                </MDButton>
              </MDBox>
              <MDBox p={3} display="flex" justifyContent="center" alignItems="center" minHeight="50svh">
                {loading ? (
                  <CircularProgress color="primary" />
                ) : (
                  <DataTable
                    table={{ columns: columns, rows: paginatedTableData }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                    showTotalEntries={true}
                    noEndBorder
                    pagination={{
                      variant: "contained",
                      color: "dark",
                      page: page,
                      onPageChange: handlePageChange,
                      rowsPerPage: rowsPerPage,
                      onRowsPerPageChange: handleRowsPerPageChange,
                      count: filteredCategories.length,
                    }}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <BusinessCategoryAddModel open={openAddModal} handleClose={handleCloseAddModal} />

      <BusinessCategoryEditModel
        open={openEditModal}
        handleClose={handleClose}
        editInput={editInput}
        handleEditChange={handleEditChange}
      />

      <Dialog open={openDeleteModal} onClose={handleClose}>
        <MDBox component="form" onSubmit={deleteBusinessCategory}>
          <DialogTitle>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <WarningIcon style={{ color: red[700], fontSize: 40 }} />
              </Grid>
              <Grid item>
                <MDTypography variant="h6" component="span">
                  Confirm Delete
                </MDTypography>
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2} style={{ backgroundColor: red[50], borderLeft: `5px solid ${red[700]}` }}>
              <MDTypography variant="body1">
                Are you sure you want to delete this business category? This action cannot be undone.
              </MDTypography>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleClose} style={{ color: "gray" }}>
              Cancel
            </MDButton>
            <MDButton type="submit" color="primary">
              Delete
            </MDButton>
          </DialogActions>
        </MDBox>
      </Dialog>
    </DashboardLayout>
  );
}